import { Group, Mesh } from 'three';
import { Component3D } from './Component3D';
import { SIZE_OPTIONS, SIZE_REFS_MAP, UNIT } from '../../../constants/sizes';
import {
    getCommentMetrics, getTextLayoutStyle, getTextMetrics, getTitleMetrics
} from './metrics/QuestionMetrics';
import {
    COLOR_DEFAULT_TEXT
} from '../../../constants/colors';
import Text3D from './Text3D';
import { MaterialsFactory } from '../factories/MaterialsFactory';
import { materialsLib } from '../../../../data/settings';
import { RoundedRect } from '../geometries/RoundedRect';

// import Text3D from "./Text3D";

export class CommentText3D extends Component3D {
    #text;
    #pagination;
    #titleG;
    #backG;
    #title3d;
    #pagination3d;
    #titleBack3d;
    #active = true;
    #orientation = SIZE_OPTIONS.LANDSCAPE;
    #width = -1;
    #height = -1;

    constructor(text, index, numTotal) {
        super();

        this.#text = text;
        this.#pagination = `${index + 1} из ${numTotal}`;

        const titleG = new Group();
        const backG = new Group();

        titleG.position.set(0, UNIT.questionY);
        backG.position.set(0, UNIT.questionY);

        this.add(titleG);
        this.add(backG);

        this.#titleG = titleG;
        this.#backG = backG;
    }

    _setup() {
        this.waitForPreload = true;
        this.hasResize = true;
    }

    _resize(winWidth, winHeight, orientation, orientationChanged, changed, pixelRatio) {
        super._resize(winWidth, winHeight, orientation, orientationChanged, changed, pixelRatio);

        if (orientationChanged) {
            const { questionYCorrection, questionScale } = SIZE_REFS_MAP[orientation];

            this.#titleG.position.set(0, UNIT.questionY + questionYCorrection, 0);
            this.#backG.position.set(0, UNIT.questionY + questionYCorrection, 0);

            this.#titleG.scale.set(questionScale, questionScale, questionScale);
            this.#backG.scale.set(questionScale, questionScale, questionScale);

            this.#orientation = orientation;

            this._updateTextMetricsAndPosition(orientation);
        }
    }

    setupPreloadedContent(infrastructure) {
        const metricsTtl = getTitleMetrics(SIZE_OPTIONS.LANDSCAPE);
        const metricsCmt = getCommentMetrics(SIZE_OPTIONS.LANDSCAPE);
        const metricsTxt = getTextMetrics(SIZE_OPTIONS.LANDSCAPE);
        const metricsLayout = getTextLayoutStyle(SIZE_OPTIONS.LANDSCAPE);
        const {
            commentDeltaWidth: deltaWidth, commentDeltaHeight: deltaHeight, commentMinHeight, roundRadius
        } = metricsLayout;
        const { questionScale } = SIZE_REFS_MAP[SIZE_OPTIONS.LANDSCAPE];

        // TITLE
        const title3d = new Text3D({
            ...metricsCmt,
            color: COLOR_DEFAULT_TEXT,
            valign: 'bottom',
            type: 'textMaterial2Bent',
            bendingRadius: UNIT.questionR
        }, this.#text, infrastructure);
        const pagination3d = new Text3D({
            ...metricsTxt,
            width: metricsTtl.width * 1.04,
            color: COLOR_DEFAULT_TEXT,
            valign: 'bottom',
            type: 'textMaterial2Bent',
            bendingRadius: UNIT.questionR
        }, this.#pagination, infrastructure);
        const { lineHeight: ttlLineHeight } = title3d;
        const r = ttlLineHeight * roundRadius;
        const w = title3d.textWidth + ttlLineHeight * deltaWidth;
        const h = Math.max(title3d.textHeight + ttlLineHeight * deltaHeight, commentMinHeight * ttlLineHeight);

        const geom = new RoundedRect(
            w,
            h,
            r,
            UNIT.questionR,
            10,
            40
        );

        const titleBack3d = new Mesh(
            geom,
            MaterialsFactory.create(materialsLib.commentBackBasic)
        );

        // TITLE POSITION
        title3d.position.y = 0.5 * (h - title3d.textHeight);
        title3d.position.z = 0.1;
        pagination3d.position.y = h + pagination3d.lineHeight * 0.5;
        geom.translate(0, 0.5 * h, 0);

        this.#titleG.add(titleBack3d);
        this.#titleG.add(title3d);
        this.#titleG.add(pagination3d);

        this.#title3d = title3d;
        this.#pagination3d = pagination3d;
        this.#titleBack3d = titleBack3d;

        this.#width = w * questionScale;
        this.#height = h * questionScale;
    }

    disable() {
        this.#active = false;
        this._offInteraction();
    }

    _updateTextMetricsAndPosition(orientation) {
        const metricsTtl = getTitleMetrics(orientation);
        const metricsCmt = getCommentMetrics(orientation);
        const metricsTxt = getTextMetrics(orientation);
        const metricsLayout = getTextLayoutStyle(orientation);
        const {
            commentDeltaWidth: deltaWidth, commentDeltaHeight: deltaHeight, commentMinHeight, roundRadius
        } = metricsLayout;
        const { questionScale } = SIZE_REFS_MAP[orientation];
        const title3d = this.#title3d;
        const pagination3d = this.#pagination3d;
        let titleBack3d = this.#titleBack3d;

        this.#title3d.setMetrics({
            ...metricsCmt,
            valign: 'bottom'
        });

        this.#pagination3d.setMetrics({
            ...metricsTxt,
            width: metricsTtl.width * 1.04,
            valign: 'bottom'
        });

        const { lineHeight: ttlLineHeight } = this.#title3d;
        const r = ttlLineHeight * roundRadius;
        const w = this.#title3d.textWidth + ttlLineHeight * deltaWidth;
        const h = Math.max(this.#title3d.textHeight + ttlLineHeight * deltaHeight, commentMinHeight * ttlLineHeight);

        if (titleBack3d) this.#titleG.remove(titleBack3d);

        const geom = new RoundedRect(
            w,
            h,
            r,
            UNIT.questionR,
            10,
            40
        );

        titleBack3d = new Mesh(
            geom,
            MaterialsFactory.create(materialsLib.commentBackBasic)
        );

        // TITLE POSITION
        title3d.position.y = 0.5 * (h - title3d.textHeight);
        pagination3d.position.y = h + pagination3d.lineHeight * 0.5;
        geom.translate(0, 0.5 * h, 0);

        this.#titleG.add(titleBack3d);

        this.#titleBack3d = titleBack3d;

        this.#width = w * questionScale;
        this.#height = h * questionScale;
    }

    get width() {
        return this.#width;
    }

    get height() {
        return this.#height;
    }

    // eslint-disable-next-line class-methods-use-this
    get radius() {
        return UNIT.questionR;
    }

    // eslint-disable-next-line class-methods-use-this
    get shiftY() {
        const { questionYCorrection } = SIZE_REFS_MAP[this.#orientation];

        return UNIT.questionY + questionYCorrection;
    }
}
